import { RealRewardStep } from "@hg/activity-utils"
import Modal from "@hg/modal"
import cls from "classnames"
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"

import OutlineButton from "@/components/OutlineButton"
import Toast from "@/components/Toast"
import { i18n } from "@/i18n"
import { requestPostContact } from "@/services/activity"
import { Code2Msg } from "@/Topics/Recall/helper"

import RewardWrapper from "../RewardWrapper"
import styles from "./index.module.scss"

import type { ContactInfo } from "@/services/activity"

const AddressContainer = Modal.createContainer("__ADDRESS_CONTAINER")
AddressContainer.rootElem.style.zIndex = "999"

export interface EditAddressModalProps {
    open: boolean
    onClose: () => void
    contactInfo?: ContactInfo | null | undefined
    customTitle?: React.ReactNode
    step?: RealRewardStep
    loading?: boolean
    coupon?: string
    onContactChange?: (contact: ContactInfo) => void
    onStepChange?: (step: RealRewardStep) => void
    onSubmitContact?: () => void
    onRefresh?: () => void
    noModal?: boolean
    useTempAddress?: boolean
}

// ─── 表单状态 ─────────────────────────────────────────────────────────────────

type FormState = {
    name: string
    phone: string
    address: string
    postcode: string
    email: string
}

const EMPTY_FORM: FormState = {
    name: "",
    phone: "",
    address: "",
    postcode: "",
    email: "",
}

function contactInfoToForm(ci?: ContactInfo | null): FormState {
    if (!ci) return EMPTY_FORM
    return {
        name: ci.name ?? "",
        phone: ci.phone ?? "",
        address: ci.address ?? "",
        postcode: ci.postcode ?? "",
        email: ci.email ?? "",
    }
}

function formToContactInfo(form: FormState): ContactInfo {
    return {
        name: form.name,
        phone: form.phone,
        address: form.address,
        postcode: form.postcode,
        email: form.email,
        province: "",
        city: "",
        county: "",
    }
}

// ─── バリデーション ────────────────────────────────────────────────────────────

function validateForm(form: FormState): string | null {
    if (!form.name.trim()) return i18n.get("reward.error_name_empty")
    if (!form.phone) return i18n.get("reward.error_phone_empty")
    if (!/^\d+$/.test(form.phone)) return i18n.get("reward.error_phone_format")
    if (!form.address.trim()) return i18n.get("reward.error_address_empty")
    if (!form.postcode.trim()) return i18n.get("reward.error_postcode_empty")
    if (!form.email) return i18n.get("reward.error_email_empty")
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return i18n.get("reward.error_email_format")
    return null
}

// ─── コンポーネント ────────────────────────────────────────────────────────────

export const EditAddressModal: FC<EditAddressModalProps> = ({
    open,
    onClose,
    contactInfo,
    customTitle,
    step: originalStep,
    loading: originalLoading,
    coupon,
    onContactChange,
    onStepChange,
    onSubmitContact,
    onRefresh,
    noModal,
}) => {
    const [step, setStep] = useState<RealRewardStep>(
        originalStep ||
            (contactInfo ? RealRewardStep.PREVIEW : RealRewardStep.EDIT),
    )
    const [form, setForm] = useState<FormState>(contactInfoToForm(contactInfo))
    const [loading, setLoading] = useState(originalLoading)

    const handleStepChange = useCallback(
        (s: RealRewardStep) => {
            setStep(s)
            onStepChange?.(s)
        },
        [onStepChange],
    )

    useEffect(() => {
        if (originalStep !== undefined) setStep(originalStep)
        if (contactInfo) {
            setForm(contactInfoToForm(contactInfo))
            if (!originalStep) setStep(RealRewardStep.PREVIEW)
        }
        if (typeof originalLoading === "boolean") setLoading(originalLoading)
    }, [originalStep, contactInfo, originalLoading])

    useEffect(() => {
        if (!open) {
            handleStepChange(
                contactInfo ? RealRewardStep.PREVIEW : RealRewardStep.EDIT,
            )
        }
    }, [open, contactInfo, handleStepChange])

    const updateField = (key: keyof FormState, value: string) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value }
            onContactChange?.(formToContactInfo(next))
            return next
        })
    }

    const handleSubmitContact = async () => {
        const err = validateForm(form)
        if (err) {
            Toast.message(err)
            return
        }
        if (onSubmitContact) {
            onSubmitContact()
            return
        }
        setLoading(true)
        try {
            const res = await requestPostContact(coupon ?? "", formToContactInfo(form))
            if (res.code === 0) {
                onRefresh?.()
                handleStepChange(RealRewardStep.DONE)
            } else {
                Toast.message(Code2Msg(res.code) || res.message)
            }
        } catch (error: unknown) {
            Toast.message(
                error instanceof Error ? error.message : String(error) || "-",
            )
        } finally {
            setLoading(false)
        }
    }

    const modalTip = useMemo(
        () =>
            !contactInfo
                ? i18n.getRecall("award.edit_notice")
                : i18n.getRecall("award.show_notice"),
        [contactInfo],
    )

    const FIELDS: { key: keyof FormState; label: string; wide?: boolean }[] = [
        { key: "name",     label: i18n.get("reward.placeholder_name") },
        { key: "phone",    label: i18n.get("reward.placeholder_phone") },
        { key: "postcode", label: i18n.get("reward.placeholder_postcode") },
        { key: "email",    label: i18n.get("reward.placeholder_email") },
        { key: "address",  label: i18n.get("reward.placeholder_address"), wide: true },
    ]

    const content = (
        <RewardWrapper onClose={onClose} showRuleButton={true} showTitle>
            <div className={cls(styles.main, styles.fadeContent)}>
                <div className={styles.prize}>
                    {step === RealRewardStep.EDIT && (
                        <>
                            {customTitle ? (
                                customTitle
                            ) : (
                                <div className={styles.title}>{modalTip}</div>
                            )}
                            <div className={styles.addList}>
                                {FIELDS.map(({ key, label, wide }) => (
                                    <div key={key} className={cls(styles.inputGroup, wide && styles.wide)}>
                                        <div className={styles.label}>{label}</div>
                                        <input
                                            className={styles.fieldInput}
                                            value={form[key]}
                                            placeholder={label}
                                            onChange={(e) =>
                                                updateField(key, e.target.value)
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className={styles.btnGroup}>
                                <OutlineButton
                                    className={styles.btn}
                                    text={i18n.getRecall("poster.button_next")}
                                    onClick={() => {
                                        const err = validateForm(form)
                                        if (err) {
                                            Toast.message(err)
                                            return
                                        }
                                        handleStepChange(RealRewardStep.CONFIRM)
                                    }}
                                    fontSize={40}
                                    strokeWidth={10}
                                    strokeColor="#872D19"
                                    fillGradient={{
                                        from: "#FFFAF6",
                                        to: "#FCF1D2",
                                    }}
                                />
                            </div>
                        </>
                    )}

                    {step === RealRewardStep.CONFIRM && (
                        <>
                            <div
                                className={styles.addressPreview}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: i18n
                                dangerouslySetInnerHTML={{
                                    __html: i18n.getRecall(
                                        "award.address_preview_notice",
                                    ),
                                }}
                            />
                            <div className={styles.addList}>
                                {FIELDS.map(({ key, label, wide }) => (
                                    <div key={key} className={cls(styles.inputGroup, wide && styles.wide)}>
                                        <div className={styles.label}>{label}</div>
                                        <div className={styles.text}>{form[key]}</div>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.btnGroup}>
                                <OutlineButton
                                    className={cls(styles.btn, styles.return)}
                                    text={i18n.get("reward.button_return")}
                                    onClick={() =>
                                        handleStepChange(RealRewardStep.EDIT)
                                    }
                                    fontSize={46}
                                    strokeWidth={10}
                                    strokeColor="#1F5A5F"
                                />
                                <OutlineButton
                                    className={styles.btn}
                                    text={i18n.get("reward.button_confirm")}
                                    onClick={handleSubmitContact}
                                    disabled={loading}
                                    fontSize={46}
                                    strokeWidth={10}
                                    strokeColor="#872D19"
                                />
                            </div>
                        </>
                    )}

                    {step === RealRewardStep.DONE && (
                        <div className={styles.done}>
                            <div
                                className={styles.intro}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: i18n
                                dangerouslySetInnerHTML={{
                                    __html: i18n.get("reward.text_introduction"),
                                }}
                            />
                        </div>
                    )}

                    {step === RealRewardStep.PREVIEW && (
                        <>
                            <div
                                className={styles.addressPreview}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: i18n
                                dangerouslySetInnerHTML={{
                                    __html: i18n.getRecall(
                                        "award.address_preview_notice",
                                    ),
                                }}
                            />
                            <div className={styles.addList}>
                                {FIELDS.map(({ key, label, wide }) => (
                                    <div key={key} className={cls(styles.inputGroup, wide && styles.wide)}>
                                        <div className={styles.label}>{label}</div>
                                        <div className={styles.text}>{form[key]}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </RewardWrapper>
    )

    return noModal ? (
        content
    ) : (
        <Modal
            active={open}
            onClose={onClose}
            maskClosable
            contentClassName={styles.addressModal}
            container={AddressContainer}
        >
            {content}
        </Modal>
    )
}
